import React from 'react';
import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  NOTIFICATION_STATUS_COLOR,
  NotificationChannelType,
  NotificationStatus,
  notificationChannelLabel,
  notificationStatusLabel,
} from './labels';

// Bloco "Notificação" de UM nível da campanha (spec — CLAUDE.md, pedido do Emílio em 30/09:
// "nessa tela não fala sobre a notificação push, se já foi enviada ou não, se foi criada ou não").
// Usa o `notification` que o backend já calcula (docs/CUSTOMER_RECOVERY_CAMPAIGN.md do
// pontuax-backend, seção "Status da notificação (push/sms) por nível") — nenhuma lógica de
// status é recalculada aqui, só formatação.

type NotificationCounters = { dispatched: number; received: number; read: number; clicked: number; errors: number };

type NotificationChannelInfo = {
  type: NotificationChannelType;
  dispatchMessageId: string;
  status: NotificationStatus;
  sendAt: string | null;
  cardinality: number;
  countersAvailable: boolean;
  counters: NotificationCounters;
};

export type LevelNotificationInfo = {
  configured: boolean;
  status: NotificationStatus;
  event: { sendAt: string | null; recipients: number; skipped: boolean; skippedReason: string | null } | null;
  channels: NotificationChannelInfo[];
};

const numberFmt = (n: number) => Number(n || 0).toLocaleString('pt-BR');

// Abre a tela de envio já existente (dispatchMessage) numa aba nova — o admin usa hash router e
// guarda a rede selecionada no localStorage, então a aba nova já abre na mesma rede (mesmo padrão
// de `openInNewTab` do Painel 360, ver feedback_painel_atalhos_nova_aba).
const openDispatchMessage = (dispatchMessageId: string) => {
  window.open(`#/chains/chainId/dispatch-messages/${dispatchMessageId}/show`, '_blank', 'noopener');
};

const ChannelCounters: React.FC<{ channel: NotificationChannelInfo }> = ({ channel }) => {
  if (!channel.countersAvailable) {
    return (
      <Typography variant="caption" color="text.secondary" component="div">
        Destinatários {numberFmt(channel.cardinality)} — detalhamento de entrega não disponível para SMS.
      </Typography>
    );
  }

  const gap = Math.max(0, (channel.cardinality || 0) - (channel.counters.dispatched || 0));

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div">
        Destinatários {numberFmt(channel.cardinality)} · Disparados {numberFmt(channel.counters.dispatched)} ·{' '}
        Recebidos {numberFmt(channel.counters.received)} · Lidos {numberFmt(channel.counters.read)} ·{' '}
        Cliques {numberFmt(channel.counters.clicked)} · Não entregues {numberFmt(channel.counters.errors)}
      </Typography>
      {gap > 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ fontStyle: 'italic' }}>
          {numberFmt(gap)} cliente(s) não receberam: sem o app instalado ou com a notificação desligada no celular.
        </Typography>
      )}
    </Box>
  );
};

const ChannelRow: React.FC<{ channel: NotificationChannelInfo }> = ({ channel }) => (
  <Box sx={{ mt: 1 }}>
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
      <Typography variant="body2" fontWeight={600}>
        {notificationChannelLabel(channel.type)}
      </Typography>
      <Chip
        size="small"
        label={notificationStatusLabel(channel.status, channel.sendAt)}
        color={NOTIFICATION_STATUS_COLOR[channel.status] as any}
      />
    </Stack>
    <ChannelCounters channel={channel} />
    <Link
      component="button"
      type="button"
      variant="caption"
      onClick={() => openDispatchMessage(channel.dispatchMessageId)}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
    >
      Ver detalhes do envio <OpenInNewIcon sx={{ fontSize: 14 }} />
    </Link>
  </Box>
);

const SIMPLE_STATUSES: NotificationStatus[] = ['not_configured', 'no_active_recipients', 'scheduling_failed', 'not_started'];

const NotificationBlock: React.FC<{ notification: LevelNotificationInfo | null | undefined }> = ({ notification }) => {
  if (!notification) return null;

  const isSimple = SIMPLE_STATUSES.includes(notification.status);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <NotificationsActiveIcon fontSize="small" color="action" />
        <Typography variant="caption" color="text.secondary">Notificação</Typography>
      </Stack>

      {isSimple ? (
        <Chip
          size="small"
          label={notificationStatusLabel(notification.status, notification.event?.sendAt)}
          color={NOTIFICATION_STATUS_COLOR[notification.status] as any}
          sx={{ mt: 0.5 }}
        />
      ) : (
        notification.channels.map(channel => <ChannelRow key={channel.dispatchMessageId} channel={channel} />)
      )}
    </Box>
  );
};

export default NotificationBlock;
